import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { SkButtonComponent, SkIconComponent } from '@mercadoni/stark-ui';

import { LEAPBOT_LABELS } from '../../core/config/leapbot.config';
import type { LeapBotDraftAttachment, LeapBotMessage } from '../../core/interfaces/leapbot.interface';
import { AuthService } from '../../core/services/auth/auth.service';
import { LeapBotChatService } from '../../core/services/leapbot/leapbot-chat.service';
import { LeapBotSoundService } from '../../core/services/leapbot/leapbot-sound.service';
import { DialogStackService } from '../../core/services/ui/dialog-stack.service';
import { captureFocus } from '../../shared/utils/focus-return.util';
import { sourcesStepLabel, toolStepLabel } from '../../shared/utils/leapbot-chat.util';
import { formatFileSize, LEAPBOT_FILE_ACCEPT } from '../../shared/utils/leapbot-files.util';
import { groupConversations } from '../../shared/utils/leapbot-history.util';
import { type LeapBotBlock, parseMarkdown } from '../../shared/utils/leapbot-markdown.util';
import { NfLeapBotInlinesComponent } from './leapbot-inlines.component';

const COPIED_FEEDBACK_MS = 2_000;
const PREVIEW_DIALOG_ID = 'leapbot-attachment-preview';
const PREVIEW_TEXT_LIMIT = 100_000;

/** What the attachment preview dialog shows. */
interface AttachmentPreview {
  attachment: LeapBotDraftAttachment;
  kind: 'image' | 'video' | 'json';
  /** Object URL for images and videos; revoked when the preview closes. */
  url: string | null;
  /** Pretty-printed JSON text, cut to a safe size. */
  text: string | null;
}

/**
 * LeapBot chat shown as a floating window at the bottom-right of the screen: welcome with suggestions, conversation with
 * streamed Markdown answers, work steps and sources, ticket and action cards, history with
 * search, and the message composer. It only renders state owned by `LeapBotChatService` and
 * forwards user actions to it.
 */
@Component({
  selector: 'nf-leapbot-chat',
  imports: [DatePipe, ReactiveFormsModule, SkButtonComponent, SkIconComponent, NfLeapBotInlinesComponent],
  templateUrl: './leapbot-chat.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // The window is fixed to the screen, so the host takes no space in the shell layout.
    class: 'contents',
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class NfLeapBotChatComponent {
  /** Whether the panel is open. */
  readonly isOpen = input<boolean>(false);


  /** Emitted when the panel asks to close (close button or Escape). */
  readonly close = output<void>();

  protected readonly chat = inject(LeapBotChatService);
  protected readonly sound = inject(LeapBotSoundService);
  private readonly auth = inject(AuthService);
  private readonly dialogStack = inject(DialogStackService);
  protected readonly labels = LEAPBOT_LABELS;

  protected readonly messageControl = new FormControl<string>('', { nonNullable: true });
  private readonly inputValue = toSignal(this.messageControl.valueChanges, { initialValue: '' });
  private readonly inputElement = viewChild<ElementRef<HTMLTextAreaElement>>('messageInput');
  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  private readonly previewClose = viewChild<ElementRef<HTMLButtonElement>>('previewClose');
  protected readonly fileAccept = LEAPBOT_FILE_ACCEPT;
  protected readonly isDragging = signal(false);
  protected readonly showRecordNotice = signal(false);
  protected readonly preview = signal<AttachmentPreview | null>(null);
  private restorePreviewFocus: (() => void) | null = null;

  protected readonly historyQuery = new FormControl<string>('', { nonNullable: true });
  private readonly historyQueryValue = toSignal(this.historyQuery.valueChanges, { initialValue: '' });
  protected readonly historyOpen = signal(false);
  protected readonly pendingDeleteId = signal<string | null>(null);
  protected readonly authorizeClicked = signal(false);
  protected readonly copyState = signal<{ key: string; ok: boolean } | null>(null);
  private copyTimer: ReturnType<typeof setTimeout> | null = null;

  protected readonly canSend = computed(
    () =>
      (this.inputValue().trim().length > 0 || this.chat.attachments().length > 0) &&
      !this.chat.isBusy() &&
      this.chat.isEnabled(),
  );

  /** Time recorded so far as `mm:ss`. */
  protected readonly elapsedLabel = computed((): string => {
    const seconds = this.chat.recordingSeconds();
    return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  });

  /** First name of the signed-in user for the greeting, or `null` when the profile has none. */
  protected readonly firstName = computed((): string | null => {
    const name = this.auth.user()?.name?.trim() ?? '';
    return name.length > 0 && !name.includes('@') ? name.split(/\s+/)[0] : null;
  });

  protected readonly historyGroups = computed(() =>
    groupConversations(this.chat.conversations(), this.historyQueryValue(), new Date()),
  );

  protected readonly lastMessageId = computed((): string | null => {
    const messages = this.chat.messages();
    return messages.length > 0 ? messages[messages.length - 1].id : null;
  });

  /** Text announced to screen readers once an answer is complete, never token by token. */
  protected readonly announcement = computed((): string => {
    const messages = this.chat.messages();
    const last = messages[messages.length - 1];
    return last && last.role === 'assistant' && last.isComplete ? last.content : '';
  });

  constructor() {
    // A failed send puts the text back in the input; focus moves to the input when the panel opens.
    effect(() => {
      const draft = this.chat.draft();
      if (draft.length > 0 && draft !== this.messageControl.value) this.messageControl.setValue(draft);
    });
    effect(() => {
      if (this.isOpen()) setTimeout(() => this.inputElement()?.nativeElement.focus());
    });
    // The close button only exists while the preview is open: focus it as soon as it renders.
    effect(() => this.previewClose()?.nativeElement.focus());
    // Keep the service draft in step with what the user types, so `openWithContext` can append to it.
    this.messageControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => this.chat.setDraft(value));
    inject(DestroyRef).onDestroy(() => {
      if (this.copyTimer) clearTimeout(this.copyTimer);
      this.closePreview();
    });
  }

  protected fileSize(bytes: number): string {
    return formatFileSize(bytes);
  }

  protected canPreview(attachment: { type: string; name: string }): boolean {
    return this.previewKind(attachment) !== null;
  }

  protected onRecordClick(): void {
    if (this.chat.canRecord()) this.showRecordNotice.set(true);
  }

  protected async confirmRecord(): Promise<void> {
    this.showRecordNotice.set(false);
    await this.chat.startRecording();
  }

  protected async onStopRecording(): Promise<void> {
    await this.chat.stopRecording();
  }

  /** Closes the chat; a recording in progress is discarded. */
  protected onClose(): void {
    this.showRecordNotice.set(false);
    this.chat.cancelRecording();
    this.close.emit();
  }

  protected openFilePicker(): void {
    this.fileInput()?.nativeElement.click();
  }

  protected onFilesSelected(event: Event): void {
    const element = event.target as HTMLInputElement; // the hidden <input type="file"> that fired the event
    this.chat.addFiles(Array.from(element.files ?? []));
    element.value = '';
  }

  protected onDragOver(event: DragEvent): void {
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    this.isDragging.set(true);
  }

  protected onDragLeave(event: DragEvent): void {
    const aside = event.currentTarget as HTMLElement; // the panel the listener is bound to
    if (!aside.contains(event.relatedTarget as Node | null)) this.isDragging.set(false);
  }

  protected onDrop(event: DragEvent): void {
    if (!event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();
    this.isDragging.set(false);
    this.chat.addFiles(Array.from(event.dataTransfer.files));
  }

  protected onPaste(event: ClipboardEvent): void {
    const files = Array.from(event.clipboardData?.files ?? []);
    if (files.length === 0) return;
    event.preventDefault();
    this.chat.addFiles(files);
  }

  protected async openPreview(attachment: LeapBotDraftAttachment): Promise<void> {
    const kind = this.previewKind(attachment);
    if (!kind) return;
    this.closePreview(false);
    this.restorePreviewFocus = captureFocus(() => this.inputElement()?.nativeElement ?? null);
    let url: string | null = null;
    let text: string | null = null;
    if (kind === 'json') {
      const raw = (await attachment.file.text()).slice(0, PREVIEW_TEXT_LIMIT);
      try {
        text = JSON.stringify(JSON.parse(raw), null, 2);
      } catch {
        text = raw;
      }
    } else {
      url = URL.createObjectURL(attachment.file);
    }
    this.dialogStack.push(PREVIEW_DIALOG_ID);
    this.preview.set({ attachment, kind, url, text });
  }

  protected closePreview(restoreFocus = true): void {
    const current = this.preview();
    if (!current) return;
    if (current.url) URL.revokeObjectURL(current.url);
    this.preview.set(null);
    this.dialogStack.remove(PREVIEW_DIALOG_ID);
    if (restoreFocus) this.restorePreviewFocus?.();
    this.restorePreviewFocus = null;
  }

  private previewKind(attachment: { type: string; name: string }): AttachmentPreview['kind'] | null {
    const type = attachment.type.split(';')[0].toLowerCase();
    if (type.startsWith('image/')) return 'image';
    if (type.startsWith('video/')) return 'video';
    if (type === 'application/json' || attachment.name.toLowerCase().endsWith('.json')) return 'json';
    return null;
  }

  protected blocks(message: LeapBotMessage): LeapBotBlock[] {
    return parseMarkdown(message.content);
  }

  protected sourcesLabel(count: number): string {
    return sourcesStepLabel(count);
  }

  protected toolLabel(tool: string): string {
    return toolStepLabel(tool);
  }

  protected copyLabel(key: string, idle: string): string {
    const state = this.copyState();
    if (state?.key !== key) return idle;
    return state.ok ? this.labels.copied : this.labels.copyFailed;
  }

  protected async copy(key: string, text: string): Promise<void> {
    let ok = true;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      ok = false;
    }
    this.copyState.set({ key, ok });
    if (this.copyTimer) clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => this.copyState.set(null), COPIED_FEEDBACK_MS);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    void this.onSend();
  }

  protected async onSend(): Promise<void> {
    if (!this.canSend()) return;
    const text = this.messageControl.value;
    this.messageControl.setValue('');
    this.chat.setDraft('');
    await this.chat.send(text);
  }

  protected async onSuggestion(message: string): Promise<void> {
    if (this.chat.isBusy() || !this.chat.isEnabled()) return;
    await this.chat.send(message);
  }

  protected onNewConversation(): void {
    this.chat.newConversation();
    this.historyOpen.set(false);
    this.messageControl.setValue('');
  }

  protected toggleHistory(): void {
    this.pendingDeleteId.set(null);
    this.historyQuery.setValue('');
    this.historyOpen.update((open) => !open);
  }

  protected openConversation(id: string): void {
    this.chat.openConversation(id);
    this.historyOpen.set(false);
  }

  protected confirmDelete(id: string): void {
    this.chat.deleteConversation(id);
    this.pendingDeleteId.set(null);
  }

  protected onAuthorize(): void {
    this.authorizeClicked.set(true);
  }

  protected async onJiraAuthorized(): Promise<void> {
    this.authorizeClicked.set(false);
    await this.chat.confirmJiraAuthorization();
  }

  protected onEscape(): void {
    if (!this.isOpen()) return;
    // A preview is the topmost layer: Escape closes only it.
    if (this.preview() !== null) {
      this.closePreview();
      return;
    }
    if (this.dialogStack.hasOpenDialog()) return;
    // The innermost layer closes first: a pending delete confirmation, then the chat itself.
    if (this.pendingDeleteId() !== null) {
      this.pendingDeleteId.set(null);
      return;
    }
    this.onClose();
  }
}
